import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import {
  Briefcase,
  Settings,
  LogOut,
  Bell,
  ArrowLeft,
  Search,
  FileText,
  Building,
  MapPin,
  Clock,
  Eye,
  Download,
  Calendar,
  CheckCircle2,
  XCircle,
  AlertCircle,
  MessageSquare,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useApplicationsQuery, useWithdrawApplication } from '../../hooks/useCandidateData';
import type { ApplicationStatus, CandidateApplication } from '../../api/applications';
import { toast } from 'sonner';

type FilterStatus = 'all' | ApplicationStatus;

export function Applications() {
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: applications = [],
    isPending,
    isError,
    refetch,
  } = useApplicationsQuery();
  const withdrawMutation = useWithdrawApplication();
  const [withdrawingId, setWithdrawingId] = useState<string | number | null>(null);

  const statusOptions = useMemo(
    () => ['all', ...Array.from(new Set(applications.map((app) => app.statusType)))],
    [applications],
  );

  const filteredApplications = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return applications.filter((app) => {
      const matchesStatus = statusFilter === 'all' || app.statusType === statusFilter;
      const matchesSearch =
        !normalizedSearch ||
        app.jobTitle.toLowerCase().includes(normalizedSearch) ||
        app.company.toLowerCase().includes(normalizedSearch);
      return matchesStatus && matchesSearch;
    });
  }, [applications, statusFilter, searchTerm]);

  const stats = useMemo(
    () => [
      { label: 'Total Applications', value: applications.length },
      { label: 'Interviews', value: applications.filter((a) => a.statusType === 'interview').length },
      { label: 'Under Review', value: applications.filter((a) => a.statusType === 'reviewed' || a.statusType === 'pending').length },
      { label: 'Shortlisted', value: applications.filter((a) => a.statusType === 'shortlisted').length },
    ],
    [applications],
  );

  const handleWithdraw = (applicationId: string | number) => {
    setWithdrawingId(applicationId);
    withdrawMutation.mutate(applicationId, {
      onSuccess: () => {
        toast.success('Application withdrawn');
        refetch();
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : 'Unable to withdraw application.';
        toast.error(message);
      },
      onSettled: () => setWithdrawingId(null),
    });
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[rgba(5,9,19,0.82)] backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gold-gradient flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-black" />
              </div>
              <span className="text-gold-gradient">Lighthouse Jobs</span>
            </Link>

            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="relative text-white">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#ba9036] rounded-full" />
              </Button>
              <Link to="/life">
                <Button variant="outline" className="border-[#d2ac47]/50 text-[#ba9036] hover:bg-[#ba9036]/10">
                  Life Platform
                </Button>
              </Link>
              <Link to="/candidate/settings">
                <Button variant="ghost" size="icon" className="text-white">
                  <Settings className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/">
                <Button variant="ghost" size="icon" className="text-white">
                  <LogOut className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Link to="/candidate/dashboard">
            <Button variant="ghost" className="text-gray-400 hover:text-white mb-4 -ml-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <h1 className="mb-2 text-gold-gradient">My Applications</h1>
          <p className="text-gray-400">Track your job applications and interview status</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {isPending
            ? Array.from({ length: 4 }).map((_, index) => (
                <Card key={`stat-skeleton-${index}`} className="bg-white/5 border-white/10">
                  <CardContent className="p-6 space-y-3">
                    <div className="h-3 w-24 animate-pulse rounded bg-white/10" />
                    <div className="h-6 w-16 animate-pulse rounded bg-white/10" />
                  </CardContent>
                </Card>
              ))
            : stats.map((stat) => (
                <Card
                  key={stat.label}
                  className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300"
                >
                  <CardContent className="p-6">
                    <div className="text-white mb-1">{stat.value}</div>
                    <p className="text-sm text-gray-400">{stat.label}</p>
                  </CardContent>
                </Card>
              ))}
        </div>

        {/* Filters */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search applications..."
                  className="pl-10 bg-white/10 border-white/20 text-white"
                />
              </div>
              <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as FilterStatus)}>
                <SelectTrigger className="w-full md:w-64 bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="role-surface-candidate border-white/10 bg-role-candidate-overlay text-white backdrop-blur-xl">
                  {statusOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option === 'all' ? 'All Applications' : STATUS_LABELS[option as ApplicationStatus]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Applications List */}
        <Tabs defaultValue="all" className="space-y-6">
          <TabsList className="bg-white/5 border-white/10">
            <TabsTrigger value="all" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              All Applications
            </TabsTrigger>
            <TabsTrigger value="interviews" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              Interviews
            </TabsTrigger>
            <TabsTrigger value="shortlisted" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              Shortlisted
            </TabsTrigger>
          </TabsList>
          <TabsContent value="all" className="space-y-4">
            {isPending ? (
              <ApplicationsSkeleton />
            ) : (
              filteredApplications.map((application, index) => (
                <ApplicationCard
                  key={application.id}
                  application={application}
                  index={index}
                  onWithdraw={() => handleWithdraw(application.id)}
                  withdrawing={withdrawMutation.isPending && withdrawingId === application.id}
                />
              ))
            )}
            {filteredApplications.length === 0 && !isPending && (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-white mb-2">No applications found</h3>
                  <p className="text-gray-400 mb-4">
                    {statusFilter === 'all'
                      ? "You haven't applied to any jobs yet"
                      : `No applications with status: ${STATUS_LABELS[statusFilter as ApplicationStatus] ?? statusFilter}`}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {isError && (
          <Card className="bg-red-500/10 border border-red-500/40 mt-6">
            <CardContent className="p-4 flex items-center justify-between text-sm text-red-100">
              <span>We couldn&apos;t load your applications. Please try again.</span>
              <Button variant="outline" size="sm" className="border-red-300 text-red-200" onClick={() => refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function ApplicationCard({
  application,
  index,
  onWithdraw,
  withdrawing,
}: {
  application: CandidateApplication;
  index: number;
  onWithdraw: () => void;
  withdrawing: boolean;
}) {
  const jobSlug =
    (application as { jobSlug?: string }).jobSlug ??
    (application as { job?: { slug?: string } }).job?.slug ??
    (application.jobId ? String(application.jobId) : null);
  const jobRemoved = !jobSlug;
  const messageLink = `/candidate/messages?applicationId=${application.id}&jobId=${application.jobId}`;
  const canWithdraw = ['pending', 'reviewed', 'shortlisted'].includes(application.statusType);
  const jobStatusLabel = jobRemoved ? 'Job removed by employer' : '';

  return (
    <Card className="bg-white/5 border border-white/10 overflow-hidden">
      <div className="w-full border-l-[6px]" style={{ borderColor: index % 2 === 0 ? '#ba9036' : '#4c72ff' }} />
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-3">
                <Building className="w-8 h-8 text-[#ba9036]" />
                <div>
                  <h3 className="text-white">{application.jobTitle}</h3>
                  <p className="text-sm text-gray-400">{application.company}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-400 mt-3 flex-wrap">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {application.location}
                </span>
                {application.type && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {application.type}
                  </span>
                )}
                {application.salary && (
                  <span className="flex items-center gap-1">
                    <span className="text-[#ba9036]">{application.salary}</span>
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-gray-500 text-xs mb-1">Applied Date</p>
                <p className="text-white">{application.appliedDate}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-1">Interview Date</p>
                <p className="text-white">
                  {application.interviewDate && application.interviewTime ? (
                    <>
                      {application.interviewDate} at {application.interviewTime}
                    </>
                  ) : (
                    'TBA'
                  )}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-1">Next Step</p>
                <p className="text-white">{application.nextStep || 'Watch for updates'}</p>
              </div>
            </div>

            {application.notes && (
              <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                <p className="text-sm text-gray-300">{application.notes}</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-gray-500 text-xs mb-1">Status</p>
              <Badge className={`inline-flex items-center gap-2 ${getStatusBadge(application.statusType)}`}>
                {getStatusIcon(application.statusType)}
                {application.statusLabel}
              </Badge>
              {jobRemoved && (
                <p className="mt-2 text-xs text-red-300">{jobStatusLabel}</p>
              )}
            </div>
            <div className="flex items-center gap-1 text-sm text-gray-400">
              <Calendar className="w-4 h-4" />
              Applied on {application.appliedDate}
            </div>
            <div className="flex flex-col gap-2">
              {jobSlug ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5"
                  asChild
                >
                  <Link to={`/jobs/${jobSlug}`}>
                    <Eye className="w-4 h-4 mr-2" />
                    View Job
                  </Link>
                </Button>
              ) : (
                <Button size="sm" variant="outline" className="border-white/20 text-white" disabled>
                  <Eye className="w-4 h-4 mr-2" />
                  Job Removed
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5"
                asChild
              >
                <Link to={messageLink}>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Message employer
                </Link>
              </Button>
              <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/5">
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              {canWithdraw && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-400 text-red-200 hover:bg-red-500/10"
                  disabled={withdrawing}
                  onClick={onWithdraw}
                >
                  {withdrawing ? 'Withdrawing…' : 'Withdraw application'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const STATUS_LABELS: Record<ApplicationStatus, string> = {
  interview: 'Interview Scheduled',
  shortlisted: 'Shortlisted',
  reviewed: 'Application Reviewed',
  pending: 'Applied',
  rejected: 'Not Selected',
};

const getStatusBadge = (statusType: ApplicationStatus) => {
  switch (statusType) {
    case 'interview':
      return 'bg-green-500/20 text-green-400 border-green-500/50';
    case 'shortlisted':
      return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
    case 'reviewed':
      return 'bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50';
    case 'pending':
      return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
    case 'rejected':
      return 'bg-red-500/20 text-red-400 border-red-500/50';
    default:
      return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
  }
};

const getStatusIcon = (statusType: ApplicationStatus) => {
  switch (statusType) {
    case 'interview':
    case 'shortlisted':
      return <CheckCircle2 className="w-4 h-4" />;
    case 'rejected':
      return <XCircle className="w-4 h-4" />;
    default:
      return <AlertCircle className="w-4 h-4" />;
  }
};

function ApplicationsSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, index) => (
        <Card key={`applications-skeleton-${index}`} className="bg-white/5 border border-white/10 overflow-hidden">
          <CardContent className="p-6 space-y-4">
            <div className="h-6 w-1/3 animate-pulse rounded bg-white/10" />
            <div className="grid grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((__, innerIndex) => (
                <div key={`app-skeleton-row-${innerIndex}`} className="space-y-2">
                  <div className="h-3 w-16 animate-pulse rounded bg-white/10" />
                  <div className="h-4 w-24 animate-pulse rounded bg-white/10" />
                </div>
              ))}
            </div>
            <div className="h-16 w-full animate-pulse rounded bg-white/10" />
          </CardContent>
        </Card>
      ))}
    </>
  );
}
